/**
 * Canonical weakness taxonomy for the agent's "weaknesses" titles (NIVISION writes free text).
 * Port of WEAK_CANON in nivision-group-report/scripts/taxonomy.py with the report-13 section 6 fixes:
 * pushiness is its own code (PUSHY) instead of CLOSE_INIT, WRONG_ID needs an identity object, NEEDS catches
 * "צורך", PACE and PRODUCT_KNOW stop swallowing unrelated titles. First matching rule wins.
 */

export const WEAK_CODES = [
  'TECH',
  'NEEDS',
  'IGNORE',
  'ALTERNATIVES',
  'LANGUAGE',
  'AVAILABILITY',
  'OBJECTION',
  'EARLY_END',
  'NEXT_STEP',
  'PUSHY',
  'CLOSE_INIT',
  'CHECK_UNDERSTAND',
  'OPENING',
  'WRONG_ID',
  'FLEX',
  'PACE',
  'PATIENCE',
  'EMPATHY_TONE',
  'LISTEN',
  'NO_RAPPORT',
  'CONTROL',
  'BLAME',
  'PRODUCT_KNOW',
  'EXPLAIN',
  'PRICE_HANDLING',
  'VALUE',
  'PROCESS',
] as const;
export type WeakCode = (typeof WEAK_CODES)[number];

export const WEAK_LABEL_HE: Record<WeakCode, string> = {
  TECH: "בעיה טכנית בשמע או בקו (לא כשל נציג)",
  NEEDS: "לא העמיק בגילוי צרכי הלקוח",
  IGNORE: "התעלם מבקשה, מהעדפה או מרגש של הלקוח",
  ALTERNATIVES: "לא הציע חלופות או פתרון",
  LANGUAGE: "שפה לא פורמלית או לא מקצועית",
  AVAILABILITY: "לא בדק שהלקוח פנוי לשיחה",
  OBJECTION: "לא טיפל כראוי בהתנגדות או בחשש",
  EARLY_END: "סיים את השיחה מוקדם או לא ברור",
  NEXT_STEP: "לא סגר צעד הבא",
  PUSHY: "לחץ או התעקשות מוגזמים על הלקוח",
  CLOSE_INIT: "לא ניסה לקדם או לסגור",
  CHECK_UNDERSTAND: "לא ודא שהלקוח הבין",
  OPENING: "פתיחת שיחה או הצגה עצמית לא ברורה",
  WRONG_ID: "לא אימת עם מי הוא מדבר",
  FLEX: "חוסר גמישות או דבקות בתסריט",
  PACE: "לא התאים את הקצב או את הסגנון ללקוח",
  PATIENCE: "חוסר סבלנות",
  EMPATHY_TONE: "חוסר אמפתיה או טון לא מתאים",
  LISTEN: "לא הקשיב או קטע את הלקוח",
  NO_RAPPORT: "שיחה מכנית בלי חיבור אנושי",
  CONTROL: "לא ניהל את השיחה",
  BLAME: "העברת אחריות ללקוח",
  PRODUCT_KNOW: "חוסר ידע במוצר, במימון או בתנאים",
  EXPLAIN: "הסבר לא ברור או חסר",
  PRICE_HANDLING: "טיפול לקוי בנושא המחיר",
  VALUE: "לא הציג ערך או יתרונות",
  PROCESS: "תהליך או נהלים לא בוצעו",
};

export const WEAK_LABEL_EN: Record<WeakCode, string> = {
  TECH: "audio or line problem (not the agent)",
  NEEDS: "did not dig into the customer's needs",
  IGNORE: "ignored a request, preference or feeling of the customer",
  ALTERNATIVES: "offered no alternative or solution",
  LANGUAGE: "informal or unprofessional language",
  AVAILABILITY: "did not check the customer could talk",
  OBJECTION: "did not handle an objection or concern well",
  EARLY_END: "ended the call early or unclearly",
  NEXT_STEP: "did not close a next step",
  PUSHY: "too much pressure or insistence on the customer",
  CLOSE_INIT: "did not try to move forward or close",
  CHECK_UNDERSTAND: "did not check the customer understood",
  OPENING: "unclear opening or self-introduction",
  WRONG_ID: "did not verify who they were talking to",
  FLEX: "inflexible or stuck to the script",
  PACE: "did not match the customer's pace or style",
  PATIENCE: "impatient",
  EMPATHY_TONE: "lack of empathy or the wrong tone",
  LISTEN: "did not listen or interrupted the customer",
  NO_RAPPORT: "mechanical call without a human connection",
  CONTROL: "did not lead the call",
  BLAME: "shifted responsibility to the customer",
  PRODUCT_KNOW: "weak knowledge of the product, financing or terms",
  EXPLAIN: "unclear or missing explanation",
  PRICE_HANDLING: "handled the price topic poorly",
  VALUE: "did not present value or benefits",
  PROCESS: "process or procedure not followed",
};

export const WEAK_RULES: readonly { code: WeakCode; rx: RegExp }[] = [
  { code: 'TECH', rx: new RegExp("שמע|שמיעה|קליטה|רעש|איכות ה?קו|ניתוק|תקלה טכנית") },
  { code: 'NEEDS', rx: new RegExp("צרכ|חקירה|בירור (?:מוקדם|מקדים|מעמיק|ראשוני|צורך)|בדיקה מקדימה|בדיקה מוקדמת|העמק|התעמק|שאלות (?:פתוחות|מכוונות|הבהרה)|תשאול|הבנת (?:הצורך|המצב|הרקע|הלקוח)|חוסר בירור|קושי בהבנת|החמצת הזדמנות ל(?:בירור|העמק)|מניעים|(?:הבנת|זיהוי|בירור|גילוי|הבנה של) (?:ה)?צורך|הצעת מוצר לא רלוונטי") },
  { code: 'IGNORE', rx: new RegExp("התעלמ|לא התייחס|חוסר תגובה|לא הגיב|לא ענה ל") },
  { code: 'ALTERNATIVES', rx: new RegExp("חלופ|אלטרנטיב|פתרון|פתרונות") },
  { code: 'LANGUAGE', rx: new RegExp("שפה|סלנג|פורמלי|ניסוח לא|מילים") },
  { code: 'AVAILABILITY', rx: new RegExp("זמינות") },
  { code: 'OBJECTION', rx: new RegExp("התנגד|חשש|סירוב|התמודדות") },
  { code: 'EARLY_END', rx: new RegExp("סיום (?:שיחה |השיחה )?(?:פתאומי|מהיר|מוקדם|לא ברור|פסיבי|חלש|חד)|ויתור מהיר|ויתר|קטע את השיחה|סיום שיחה|סגירת שיחה|סיום לא|התייאש|ייאוש") },
  { code: 'NEXT_STEP', rx: new RegExp("צעד הבא|צעדים הבאים|פולואפ|מעקב|לא קבע|סיכום|תיאום המשך|המשך טיפול|שיחה חוזרת") },
  { code: 'PUSHY', rx: new RegExp("התעקשות (?:על|יתר)|לחץ (?:על ה?לקוח|מכירה|מוגזם)|דחיפה (?:אגרסיבית|מוגזמת)|אגרסיב|דחיפה$") },
  { code: 'CLOSE_INIT', rx: new RegExp("חוסר (?:ב)?(?:יוזמה|אסרטיביות|דחיפה|התעקשות)|לא ניסה (?:לסגור|לקדם)|החמצת (?:הזדמנות ל)?סגירה|קידום|מיקוד עסקי|הובלה") },
  { code: 'CHECK_UNDERSTAND', rx: new RegExp("בדיקת הבנה|אימות הבנה|וידוא הבנה|לא ודא|הנחת|הנחה שגויה") },
  { code: 'OPENING', rx: new RegExp("פתיח|הצגה עצמית|מקור הפנייה|מטרת השיחה|תכלית השיחה|הצגת מטרה|התחלה|תחילת השיחה") },
  { code: 'WRONG_ID', rx: new RegExp("זיהוי (?:ה)?לקוח|אימות (?:פרטי|זהות)|זהות|בלבול בשם|שם הלקוח") },
  { code: 'FLEX', rx: new RegExp("גמישות|נוקשות|תסריט|גמיש") },
  { code: 'PACE', rx: new RegExp("קצב|התאמת (?:ה)?(?:סגנון|קצב)") },
  { code: 'PATIENCE', rx: new RegExp("סבלנות|קוצר רוח|חוסר סבלנות|לחוץ") },
  { code: 'EMPATHY_TONE', rx: new RegExp("אמפתי|טון|רגיש|יחס|חום|אדיש|קר |להרגיע|הרגעה|תסכול|רגש") },
  { code: 'LISTEN', rx: new RegExp("הקשב|קשב|קטע את הלקוח|הפרע|מונולוג|דיבר יותר מדי|דיבור יתר") },
  { code: 'NO_RAPPORT', rx: new RegExp("אינטראקציה|אנושי|מכני|יבש|קשר אישי|חיבור|ראפור|אווירה") },
  { code: 'CONTROL', rx: new RegExp("שליטה|ניהול (?:ה)?שיחה|מבנה|לנהל|הסח|מיקוד|מבולגן|לא ממוקד|התפזרות|סדר") },
  { code: 'BLAME', rx: new RegExp("אחריות|האשמ") },
  { code: 'PRODUCT_KNOW', rx: new RegExp("חוסר ידע|לא ידע|ידע (?:מוצרי|חסר|לקוי|מוגבל)|ידע ב(?:מוצר|מימון|דגמ|ליסינג|ריבית)|בקיאות|הסבר שגוי|מידע שגוי|חוסר היכרות") },
  { code: 'EXPLAIN', rx: new RegExp("הסבר|בהירות|לא ברור|לא הסביר|לא פירט|פירוט|הבהרה|תקשורת|ניסוח|מסר") },
  { code: 'PRICE_HANDLING', rx: new RegExp("מחיר|תמחור|הצעה כספית|הנחה|עלות|כספ") },
  { code: 'VALUE', rx: new RegExp("ערך|יתרונ|תועלת|הצגת המוצר|הצגת הרכב|שכנוע|נימוק") },
  { code: 'PROCESS', rx: new RegExp("תהליך|נוהל|נהלים|תיעוד|מסמכ|פרטים|נתונים|מידע|בדיקה|עדכון|ציות|רישום|זכאות|מלאי") },
];

/** codes that mean a real conduct problem of the agent (bad-day signal d, brief weakness lines) */
export const SERIOUS_WEAK: readonly WeakCode[] = [
  'IGNORE', 'OBJECTION', 'CLOSE_INIT', 'PUSHY', 'EARLY_END', 'LISTEN', 'PATIENCE', 'EMPATHY_TONE', 'LANGUAGE', 'BLAME', 'PRODUCT_KNOW', 'NEXT_STEP',
];

/** codes the briefs may print: everything except TECH (an audio or line problem is not the agent's fault) */
export const PRINTABLE_WEAK: ReadonlySet<WeakCode> = new Set(WEAK_CODES.filter((c) => c !== 'TECH'));

/** the weakness code of one title; `null` for an empty title, `OTHER` when no rule matches */
export function canonWeakness(title: string | null | undefined): WeakCode | 'OTHER' | null {
  const t = (title ?? '').trim();
  if (!t) return null;
  for (const r of WEAK_RULES) {
    if (r.rx.test(t)) {
      if (r.code === 'NEEDS' && t.includes('התנגד')) return 'OBJECTION';
      return r.code;
    }
  }
  return 'OTHER';
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const titleOf = (it: unknown): string | null => {
  const t = typeof it === 'string' ? it : isObj(it) ? it.title : null;
  return typeof t === 'string' && t.trim() !== '' ? t.trim() : null;
};

/**
 * The weakness codes of one call's `CallDigest.agentWeaknesses` (`[{ kind: 'weakness' | 'improvement', title, description }]`):
 * weakness items only (an item without `kind` is a weakness, as the summaries' `parseWeaknesses` reads it; string items and the
 * `{ weaknesses, improvements }` object are read the same way), `canonWeakness(title)`, `null` and `OTHER` dropped, distinct in
 * first-seen order; anything else → `[]`. One parser for the bad day, the weak-call streak and the network coaching (W2-6-12).
 */
export function weakCodesOf(raw: unknown): WeakCode[] {
  const titles: string[] = [];
  const push = (it: unknown): void => {
    const t = titleOf(it);
    if (t) titles.push(t);
  };
  if (Array.isArray(raw)) {
    for (const it of raw) if (!(isObj(it) && it.kind === 'improvement')) push(it);
  } else if (isObj(raw)) {
    const src = isObj(raw.value) ? raw.value : raw;
    if (Array.isArray(src.weaknesses)) for (const it of src.weaknesses) push(it);
  }
  const out: WeakCode[] = [];
  for (const t of titles) {
    const code = canonWeakness(t);
    if (code && code !== 'OTHER' && !out.includes(code)) out.push(code);
  }
  return out;
}
